import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { ColorUtil } from '../../../utils/color-util';
import { Image } from '../Image/Image';

export type ImageCircleProps = {
  background?: 'blue' | 'light blue';
  /**
   * Fetches the picture and carries it inside the markup, which is what a page being printed to
   * PDF needs - the generator cannot follow a url out of one.
   *
   * **A screen wants the opposite.** Left off, the url goes straight to the `<img>` and the browser
   * loads it: cached, shared between pages, and needing no cross-origin permission of its own.
   */
  embed?: boolean;
  src?: string | null;
  imageName?: string | null;
  name?: string | null;
  size?: 'large' | 'default' | 'small' | 'thumbnail';
};
export const ImageCircle = ({
  background = 'blue',
  embed = false,
  src,
  imageName,
  name,
  size = 'default',
}: ImageCircleProps) => {
  const nameSplit = imageName?.split(' ') ?? [];
  const imageInitials = nameSplit.reduce((acc, word, index) => {
    return word && (index === 0 || index === nameSplit.length - 1)
      ? acc + word[0]
      : acc;
  }, '');

  return (
    <span
      className={AttrsHelper.formatClassname(
        'image-circle',
        ColorUtil.nameClass({ color: background, prefix: 'background' }),
        `size-${size}`
      )}
      data-test={name}
      data-src={src}
    >
      <Image
        alt={imageName || ''}
        /*
          Fetched and carried in the markup only when `embed` asks: a page being printed to PDF
          cannot follow a url out of it, and a screen wants the browser to load the url itself.
        */
        encode="data-url"
        fallback={
          <span data-test="INITIALS" className="image-initials">
            {imageInitials}
          </span>
        }
        imageKey={embed ? src : null}
        load={
          embed
            ? async () => {
                const response = await fetch(src ?? '');

                if (!response.ok) throw new Error(String(response.status));

                return response.blob();
              }
            : undefined
        }
        name="IMAGE"
        src={src}
      />
    </span>
  );
};
